<template>
  <v-card
    class="d-flex flex-column justify-space-between align-center"
    :class="{ 'height-full-mobile': isMobileBreakpoint }"
    flat
    tile
    min-height="366"
  >
    <div class="w-full">
      <div class="w-full d-flex justify-space-between pa-6 pr-4">
        <div class="d-flex flex-column">
          <span :class="isSubtitle ? 'body-bold-small' : 'body-bold-medium'" v-if="videos.length == 1">{{
            isSubtitle
              ? $t('message.ai.subtitles.subtitleWithAiModal.title')
              : $t('message.ai.dubbings.dubbingWithAiModal.title')
          }}</span>
          <span :class="isSubtitle ? 'body-bold-small' : 'body-bold-medium'" v-else>
            {{
              isSubtitle
                ? $t(
                    'message.ai.subtitles.subtitleWithAiModal.titleManyVideos',
                    {
                      quantity: videos.length,
                    }
                  )
                : $t('message.ai.dubbings.dubbingWithAiModal.titleManyVideos', {
                    quantity: videos.length,
                  })
            }}
          </span>
          <span class="body-regular-smaller neutral-40-light--text">
            {{
              isSubtitle
                ? $t('message.ai.subtitles.subtitleWithAiModal.description')
                : $t('message.ai.dubbings.dubbingWithAiModal.description')
            }}
            <a
              target="_blank"
              type="secondary"
              size="xsmall"
              :href="
                isSubtitle
                  ? $t('message.ai.subtitles.subtitleWithAiModal.href')
                  : $t('message.ai.dubbings.dubbingWithAiModal.href')
              "
            >
              {{ $t('message.embedModal.knowMoreInArticle') }}
              <v-icon size="12">mdi-open-in-new</v-icon>
            </a>
          </span>
        </div>
        <v-btn icon @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="container-title pa-6">
        <div class="d-flex flex-column language-select-wrapper w-full">
          <label
            class="body-label-xs mb-1 neutral-20-dark--text"
            :class="{ 'neutral-40-light--text': videos.length > 1 }"
            >{{ $t('message.ai.originalLang') }}</label
          >
          <v-tooltip v-if="videos.length > 1" left bottom>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-on="on"
                v-bind="attrs"
                class="d-flex align-center body-regular-small neutral-40-light--text mb-4"
                style="height: 40px"
                >{{ allLanguagesMapped[0].label }}</span
              ></template
            >
            <span>{{ $t('message.ai.generateToManyVideosTooltip') }}</span>
          </v-tooltip>

          <v-select
            v-else
            v-model="defaultLanguage"
            :items="allLanguagesMapped"
            append-icon="mdi-chevron-down"
            item-text="label"
            item-value="srclang"
            class="w-100 custom-placeholder no-zoom"
            placeholder="Selecione"
            :menu-props="{ top: false, offsetY: true }"
            outlined
            @change="setDefaultLanguage"
          >
            <template #prepend-inner v-if="defaultLanguage">
              <img width="16" class="mt-1 mr-2" :src="currentIcon" />
            </template>
          </v-select>
        </div>
        <div class="language-select-wrapper w-full">
          <label class="body-label-xs neutral-20-dark--text mb-1">
            {{
              isSubtitle
                ? $t('message.ai.langSubtitle')
                : $t('message.ai.langDubbing')
            }}
            <v-tooltip v-if="isHeygen" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  size="12"
                  v-on="on"
                  v-bind="attrs"
                  class="neutral-70--text"
                >
                  mdi-help-circle
                </v-icon>
              </template>
              <span class="pre-line">
                {{ $t('message.ai.tooltipLangDubbing') }}
              </span>
            </v-tooltip>
          </label>

          <div>
            <v-combobox
              v-model="selectedLanguages"
              :items="allLanguagesFilter"
              item-text="label"
              item-value="srclang"
              label="Selecione"
              chips
              multiple
              class="w-100"
              placeholder="Selecione"
              :menu-props="{ top: false, offsetY: true }"
              outlined
              ref="combobox"
              @change="validateLanguages"
            >
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-action class="mr-2 ml-2">
                    <v-checkbox
                      v-if="isLanguageAvailable(item)"
                      :input-value="isLanguageSelected(item)"
                      :color="isLanguageAvailable(item) ? 'primary' : 'grey'"
                      :disabled="!isLanguageAvailable(item)"
                    />
                    <v-checkbox
                      v-else
                      :input-value="true"
                      :color="isLanguageAvailable(item) ? 'primary' : 'grey'"
                      :disabled="!isLanguageAvailable(item)"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-tooltip :disabled="isLanguageAvailable(item)" top>
                        <template v-slot:activator="{ on }">
                          <span v-on="on">{{ item.label }}</span>
                        </template>
                        <span>
                          {{
                            isSubtitle
                              ? $t('message.ai.alreadyLanguageSubtitle')
                              : $t('message.ai.alreadyLanguageDubbing')
                          }}
                        </span>
                      </v-tooltip>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <template v-slot:selection="{ attrs, item, selected, index }">
                <v-tag
                  v-if="!isSmallDesktop"
                  class="pl-1"
                  type="neutral"
                  v-bind="attrs"
                  :input-value="selected"
                >
                  <img width="16" class="mr-1" :src="iconFlagTag(item.srclang)" />
                  {{ item.label }}
                  <v-icon @click="removeLanguage(item)" right>mdi-close</v-icon>
                </v-tag>

                <div v-else>
                  <v-tag
                    v-if="index < 2"
                    class="pl-1"
                    type="neutral"
                    v-bind="attrs"
                    :input-value="selected"
                  >
                    <img width="16" class="mr-1" :src="iconFlagTag(item.srclang)" />
                    {{ item.label }}
                    <v-icon @click="removeLanguage(item)" right>mdi-close</v-icon>
                  </v-tag>
                  <span
                    v-if="index === 2"
                    class="text-grey text-caption align-self-center"
                  >
                    (+{{ selectedLanguages.length - 2 }}
                    {{ $t('message.ai.langsLabel') }})
                  </span>
                </div>
              </template>

              <template v-slot:append>
                <v-icon
                  v-if="selectedLanguages.length"
                  class="mr-2"
                  size="16"
                  @click.stop="clearLanguages"
                >
                  mdi-close
                </v-icon>
                <v-icon @click.stop="toggleMenu">
                  {{
                    $refs.combobox && $refs.combobox.isMenuActive
                      ? 'mdi-chevron-up'
                      : 'mdi-chevron-down'
                  }}
                </v-icon>
              </template>
            </v-combobox>
          </div>
        </div>
        <div v-if="type === 'Dubbing'" class="w-full">
          <label class="body-label-xs neutral-20-dark--text mb-1">
            {{ $t('message.ai.numberOfVoices') }}
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  size="12"
                  v-on="on"
                  v-bind="attrs"
                  class="neutral-70--text"
                >
                  mdi-help-circle
                </v-icon>
              </template>
              <span class="pre-line">{{ $t('message.ai.tooltipNumberOfVoices') }}</span>
            </v-tooltip>
          </label>
          <v-select
            v-model="numberOfVoices"
            :items="numberOfVoicesOptions"
            :label="$t('message.ai.numberOfVoices')"
            :menu-props="{ contentClass: 'number-of-voices-dropdown' }"
            item-text="text"
            item-value="value"
            class="w-100"
            outlined
            return-object
          >
            <template v-slot:item="{ item, on, attrs }">
              <div
                v-on="on"
                v-bind="attrs"
                class="d-flex align-center"
                style="cursor: pointer;"
              >
                <div class="mr-3 d-flex align-center" style="width: 16px;">
                  <v-icon
                    v-if="numberOfVoices && item.value === numberOfVoices.value"
                    size="16"
                    color="primary"
                  >
                    mdi-check
                  </v-icon>
                </div>
                <div class="number-of-voices-item body-regular-small neutral-20-dark--text">
                  {{ item.text }}
                </div>
              </div>
            </template>
          </v-select>
        </div>
        <div class="w-full d-flex align-center" v-if="isElevenLabs">
          <v-checkbox v-model="cloneOriginalVoice" class="mr-1">
            <template v-slot:label>
              <span class="body-regular-small neutral-30-base--text">
                {{ $t('message.ai.cloneOriginalVoice') }}
              </span>
            </template>
          </v-checkbox>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                size="16"
                v-on="on"
                v-bind="attrs"
                class="mb-2 neutral-70--text"
              >
                mdi-help-circle
              </v-icon>
            </template>
            <span class="pre-line">{{ $t('message.ai.tooltipCloneOriginalVoice') }}</span>
          </v-tooltip>
        </div>
        <div class="w-full d-flex flex-column" v-if="isHeygen">
          <div class="d-flex align-center">
            <v-checkbox
              v-model="enableDynamicDuration"
              class="mr-1"
              :ripple="false"
            >
              <template v-slot:label>
                <span class="body-regular-small neutral-30-base--text">
                  {{ $t('message.ai.enableDynamicDuration') }}
                </span>
              </template>
            </v-checkbox>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  size="16"
                  v-on="on"
                  v-bind="attrs"
                  class="mb-2 neutral-70--text"
                >
                  mdi-help-circle
                </v-icon>
              </template>
              <span class="pre-line">{{
                $t('message.ai.tooltipEnableDynamicDuration')
              }}</span>
            </v-tooltip>
          </div>
          <div class="d-flex align-center">
            <v-checkbox
              v-model="enhanceVoiceQuality"
              class="mr-1"
              :ripple="false"
            >
              <template v-slot:label>
                <span class="body-regular-small neutral-30-base--text">
                  {{ $t('message.ai.enhanceVoiceQuality') }}
                </span>
              </template>
            </v-checkbox>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  size="16"
                  v-on="on"
                  v-bind="attrs"
                  class="mb-2 neutral-70--text"
                >
                  mdi-help-circle
                </v-icon>
              </template>
              <span class="pre-line">{{
                $t('message.ai.tooltipEnhanceVoiceQuality')
              }}</span>
            </v-tooltip>
          </div>
          <!-- <div class="d-flex align-center">
            <v-checkbox
              v-model="removeBackgroundMusic"
              class="mr-1"
              :ripple="false"
            >
              <template v-slot:label>
                <span class="body-regular-small neutral-30-base--text">
                  {{ $t('message.ai.removeBackgroundMusic') }}
                </span>
              </template>
            </v-checkbox>
          </div> -->
        </div>
        <!-- <div
          class="language-select-wrapper w-full"
          v-if="availableAiVoices.length"
        >
          <label class="body-label-xs neutral-20-dark--text mb-1">{{
            $t('message.ai.voiceDubbing')
          }}</label>
          <div>
            <template>
              <v-select
                v-model="selectedVoice"
                :items="availableAiVoices"
                append-icon="mdi-chevron-down"
                item-text="label"
                item-value="value"
                class="w-100"
                placeholder="Selecione"
                :menu-props="{ top: false, offsetY: true }"
                outlined
                @change="setVoice"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon
                    v-if="item.value !== 'original'"
                    @click.stop="handlePlayCurrentAudio(item)"
                    class="mr-2"
                  >
                    <base-btn
                      class="d-flex"
                      style="width: 24px; height: 24px; justify-content: center"
                      type="tertiary"
                      icon="mdi-play"
                    />
                  </v-list-item-icon>
                </template>
              </v-select>
            </template>
          </div>
        </div> -->
      </div>
    </div>
    <div
      class="w-full container-actions"
      :class="{ 'justify-space-between': isMobileBreakpoint }"
    >
      <div
        class="d-flex flex-column"
        :class="{ 'align-end': !isMobileBreakpoint }"
      >
        <div>
          <span class="body-bold-small"
            >Total:
            <v-icon class="creditsIcon ml-2" size="16">panda-ia-credits</v-icon>
            {{ price }}</span
          >
        </div>
        <span class="body-regular-smaller neutral-40-light--text">
          <span v-if="selectedLanguages.length">
            {{ selectedLanguages.length }}
            {{
              selectedLanguages.length > 1
                ? $t('message.ai.langsLabel')
                : $t('message.ai.langLabel')
            }}
            {{ allDurationVideos }}
          </span>
          <span v-else>{{ $t('message.ai.nothingLanguageSelected') }}</span>
        </span>
      </div>
      <v-tooltip :disabled="!!selectedLanguages.length" bottom>
        <template v-slot:activator="{ on, attrs }">
          <base-btn
            v-on="on"
            v-bind="attrs"
            type="primary"
            @click="handleNextStep"
            :disabled="!selectedLanguages.length"
          >
            {{ $t('message.ai.dubbings.modal.auto.step_one.actionBtn') }}
          </base-btn>
        </template>
        <span>{{ $t('message.ai.dubbings.tooltipDisableSelect') }}</span>
      </v-tooltip>
    </div>
  </v-card>
</template>
<script>
import { mapState } from 'vuex'
import i18n from '@/translations/lang'
import langCodesMixin from '@/utils/langCodes.mixin.js'
import globeIcon from '@/assets/pandaIcons/globe.svg'
export default {
  name: 'CheckoutAiTemplate',
  props: {
    type: {
      type: String,
      required: true,
    },
    languages: {
      type: Array,
    },
    allLanguages: {
      type: Array,
    },
    options: {
      type: Array,
    },
    price: {
      type: Number,
    },
    videos: {
      type: Array,
    },
    availableAiVoices: {
      type: Array,
      default: () => [],
    },
    alreadySelected: {
      type: Array,
    },
    tech: {
      type: String,
      default: 'elevenlabs',
    },
    existingSubtitlesImpacted: {
      type: Array,
      default: () => [],
    },
    generateDubbingData: {
      type: Object,
    },
  },
  mixins: [langCodesMixin],
  data: () => ({
    loading: false,
    menuOpen: false,
    selectedLanguages: [],
    defaultLanguage: 'auto',
    selectedVoice: 'original',
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    numberOfVoices: { value: 0, text: '' },
    cloneOriginalVoice: false,
    enableDynamicDuration: true,
    enhanceVoiceQuality: false,
    removeBackgroundMusic: false,
  }),
  watch: {
   'generateDubbingData.numberOfVoices': {
      handler(val) {
        const foundOption = this.numberOfVoicesOptions.find(option => option.value === val)
        this.numberOfVoices = foundOption || this.numberOfVoicesOptions[0]
      },
      immediate: true
    },
    'generateDubbingData.cloneOriginalVoice': {
      handler(val) {
        this.cloneOriginalVoice = val
      },
      immediate: true,
    },
    'generateDubbingData.enableDynamicDuration': {
      handler(val) {
        this.enableDynamicDuration = val
      },
      immediate: true,
    },
    'generateDubbingData.enhanceVoiceQuality': {
      handler(val) {
        this.enhanceVoiceQuality = val
      },
      immediate: true,
    },
    'generateDubbingData.removeBackgroundMusic': {
      handler(val) {
        this.removeBackgroundMusic = val
      },
      immediate: true,
    },
    'generateDubbingData.dubbingFromVideo': {
      handler(val) {
        if (val) {
          this.defaultLanguage = val
        }
      },
      immediate: true,
    },
    alreadySelected: {
      handler(val) {
        this.selectedLanguages = val || []
        this.setLanguages(this.selectedLanguages)
      },
    },
    videos() {
      if (this.videos?.length > 1) this.defaultLanguage = 'auto'
      this.setDefaultLanguage()
    },
  },
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    numberOfVoicesOptions() {
      return [
        { value: 0, text: this.$t('message.ai.detectAuto') },
        { value: 1, text: this.$t('message.ai.oneVoice') },
        { value: 2, text: this.$t('message.ai.xVoices', { count: 2 }) },
        { value: 3, text: this.$t('message.ai.xVoices', { count: 3 }) },
        { value: 4, text: this.$t('message.ai.xVoices', { count: 4 }) },
        { value: 5, text: this.$t('message.ai.xVoices', { count: 5 }) },
        { value: 6, text: this.$t('message.ai.xVoices', { count: 6 }) },
        { value: 7, text: this.$t('message.ai.xVoices', { count: 7 }) },
        { value: 8, text: this.$t('message.ai.xVoices', { count: 8 }) },
        { value: 9, text: this.$t('message.ai.xVoices', { count: 9 }) },
        { value: 10, text: this.$t('message.ai.xVoices', { count: 10 }) },
      ]
    },
    isSmallDesktop() {
      return this.windowWidth <= 1366 && this.windowHeight <= 768
    },
    allLanguagesMapped() {
      const languages = this.allLanguages.map((row) => {
        if (row.label == 'Auto') row.label = i18n.t('message.ai.detectAuto')
        return row
      })
      return languages
    },
    allLanguagesFilter() {
      return this.allLanguages.filter((row) => row.srclang !== 'auto')
    },
    currentIcon() {
      // if (!this.defaultLanguage) return null
      const country = this.getCountryCodeBySrcLang(this.defaultLanguage)
      const selectedItem = this.flagMap[country]
      return selectedItem ? selectedItem : globeIcon
    },
    isSubtitle() {
      return this.type == 'Subtitle'
    },
    isElevenLabs() {
      return this.tech === 'elevenlabs' && !this.isSubtitle
    },
    isHeygen() {
      return this.tech === 'heygen' && !this.isSubtitle
    },
    allDurationVideos() {
      let duration = 0
      this.videos.forEach((row) => {
        duration += row.length
      })
      return this.formatDuration(duration)
    },
  },
  methods: {
    updateWindowSize() {
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight
    },
    isLanguageSelected(item) {
      return this.selectedLanguages.some(
        (lang) => lang.srclang === item.srclang
      )
    },
    iconFlagTag(lang) {
      const country = this.getCountryCodeBySrcLang(lang)
      const selectedItem = this.flagMap[country]
      return selectedItem ? selectedItem : globeIcon
    },
    isLanguageAvailable(item) {
      return this.options.some((opt) => opt.srclang === item.srclang)
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen
    },
    clearLanguages() {
      this.selectedLanguages = []
      this.setLanguages()
    },
    removeLanguage(item) {
      this.selectedLanguages = this.selectedLanguages.filter(
        (lang) => lang !== item
      )
      this.$root.$emit(`selected${this.type}Item`, this.selectedLanguages)
    },
    formatDuration(seconds) {
      const allSeconds = seconds * this.selectedLanguages.length
      const totalSeconds = Math.floor(allSeconds)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const secs = totalSeconds % 60
      let result = ''
      if (hours > 0) result += `${hours}h`
      if (minutes > 0 || hours > 0) result += `${minutes}min`
      result += `${secs}s`
      return result
    },
    handleClose() {
      this.$nextTick(() => {
        this.selectedLanguages = []
      })
      this.$root.$emit('selectedVoice', [])
      this.$root.$emit(`selected${this.type}Item`, [])
      this.$root.$emit(`close${this.type}Modal`)
    },
    handleBack() {
      this.$root.$emit(`back${this.type}Modal`)
    },
    generateAiWorkFlow() {
      this.loading = true
      this.$root.$emit(`generate${this.type}`)
    },
    validateLanguages(val) {
      const validValues = this.options.map((opt) => opt.label)
      this.selectedLanguages = val?.filter((v) => validValues.includes(v.label))
      this.setLanguages(this.selectedLanguages)
    },
    handleStopCurrentAudio(item) {
      this.$root.$emit('stopCurrentAudio', item)
    },
    handlePlayCurrentAudio(item, index) {
      const data = {
        item,
        index,
      }
      this.$root.$emit('playCurrentAudio', data)
    },
    setDefaultLanguage() {
      this.$root.$emit(`default${this.type}Language`, this.defaultLanguage)
    },
    initializeDefaultLanguage() {
      if (this.videos && this.videos.length === 1) {
        const originalLang = this.videos[0].original_lang
        if (originalLang) {
          this.defaultLanguage = originalLang
        }
      }
    },
    setVoice() {
      this.$root.$emit('selectedVoice', this.selectedVoice)
    },
    setLanguages() {
      this.$root.$emit(`selected${this.type}Item`, this.selectedLanguages)
    },
    handleNextStep() {
      this.$emit('dubbingOptionsChanged', {
        numberOfVoices: this.numberOfVoices?.value || 0,
        cloneOriginalVoice: this.cloneOriginalVoice,
        enableDynamicDuration: this.enableDynamicDuration,
        enhanceVoiceQuality: this.enhanceVoiceQuality,
        removeBackgroundMusic: this.removeBackgroundMusic,
      })
      if (this.existingSubtitlesImpacted?.length > 0) {
        this.$root.$emit('handleExistingSubtitlesScreen')
      } else {
        this.$root.$emit(`checkout${this.type}Modal`)
      }
    },
  },
  beforeDestroy() {
    this.loading = false
    window.removeEventListener('resize', this.updateWindowSize)
  },
  mounted() {
    window.addEventListener('resize', this.updateWindowSize)
    this.loading = false
    if (this.videos?.length)
      this.defaultLanguage = this.videos[0].original_lang || 'auto'
    this.$nextTick(() => {
      this.selectedLanguages = this.alreadySelected || []
      this.initializeDefaultLanguage()
      this.setDefaultLanguage()
    })
  },
}
</script>
<style lang="scss" scoped>
.height-full-mobile {
  height: 100dvh;
  overflow: auto;
}
.container-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-top: 1px solid varFigma(neutral-95-lightest);
}
.w-full {
  width: 100%;
}
.container-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: varFigma(neutral-99);
  border-top: 1px solid varFigma(neutral-95-lightest);
  padding: 24px;
  gap: 16px;
}
.body-label-xs {
  color: varFigma(neutral-20-dark);
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}
.creditsIcon {
  color: varFigma(brand-70);
}
@media screen and (max-width: 768px) {
  .no-zoom >>> input {
    font-size: 16px !important;
  }

  .no-zoom >>> .v-select__selection {
    font-size: 16px !important;
  }
}
</style>
<style lang="scss">
.v-input__control fieldset {
  height: inherit !important;
  top: 0px;
  border: 1px solid varFigma(neutral-95-lightest);
  legend {
    display: none;
  }
}

.v-input__control {
  .v-chip--select {
    margin-top: 4px !important;
    margin-bottom: 4px !important;
    background: varFigma(neutral-95-lightest);
    height: 20px;
    .v-chip__content {
      font-family: Rubik;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      color: varFigma(neutral-40-light);
    }
  }
  .v-select__slot {
    font-family: Rubik;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    color: varFigma(neutral-60);
    input::placeholder {
      color: varFigma(neutral-60);
    }
    .v-label--active {
      display: none;
    }
    .v-label {
      color: varFigma(neutral-60) !important;
    }
  }
}
.neutral-70--text {
  color: varFigma(neutral-70);
}
.bg-dark {
  .v-menu__content.menuable__content__active.v-autocomplete__content
    .v-list-item::before {
    background: #061679 !important;
    color: varFigma(neutral-20-dark) !important;
  }
  .number-of-voices-dropdown .v-list-item.v-list-item--active::before {
    background-color: #061679 !important;
  }
}
.v-application
  .v-menu__content.menuable__content__active.v-autocomplete__content
  .v-list-item {
  .v-application--is-ltr .v-list-item__action:first-child {
    margin-right: 24px;
    margin-left: 12px;
  }
}
.pre-line {
  white-space: pre-line;
}
.number-of-voices-dropdown .v-list {
  padding-top: 1px;
}
.number-of-voices-dropdown .v-list-item {
  margin: 8px;
  padding: 0 12px;
  border-radius: 4px;
}
.number-of-voices-dropdown .v-list-item .number-of-voices-item {
  position: relative;
  z-index: 1;
}
.number-of-voices-dropdown .v-list-item::before {
  border-radius: 4px;
  z-index: 0;
}
</style>
